<template>
  <div class="flex flex-col gap-6 w-full text-slate-700">
    <!-- Top Bar: Period Selector & Quick Info -->
    <div
      class="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100"
    >
      <div class="flex flex-wrap items-center gap-3">
        <!-- Filter Mode Selector (Monthly vs Custom) -->
        <div
          class="inline-flex p-1 bg-slate-100 rounded-xl border border-slate-200"
        >
          <button
            type="button"
            @click="setFilterMode('month')"
            :class="[
              'px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer',
              filterMode === 'month'
                ? 'bg-white text-slate-800 shadow-xs'
                : 'text-slate-500 hover:text-slate-800',
            ]"
          >
            <i class="fa-solid fa-calendar-days mr-1 text-blue-500"></i> Monthly
          </button>
          <button
            type="button"
            @click="setFilterMode('custom')"
            :class="[
              'px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer',
              filterMode === 'custom'
                ? 'bg-white text-slate-800 shadow-xs'
                : 'text-slate-500 hover:text-slate-800',
            ]"
          >
            <i class="fa-solid fa-calendar-days mr-1 text-indigo-500"></i> Custom Range
          </button>
        </div>

        <!-- Month Dropdown (when filterMode === 'month') -->
        <div v-if="filterMode === 'month'" class="flex items-center gap-2">
          <div class="relative">
            <select
              id="month-select"
              v-model="selectedMonth"
              @change="fetchStatistics"
              class="bg-white text-slate-800 border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 appearance-none pr-8 cursor-pointer"
            >
              <option
                v-for="month in last12Months"
                :key="month.value"
                :value="month.value"
              >
                {{ month.label }}
              </option>
            </select>
            <i
              class="fa-solid fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none"
            ></i>
          </div>
        </div>

        <!-- Custom Range Inputs (when filterMode === 'custom') -->
        <div v-else class="flex items-center gap-2 flex-wrap">
          <div class="flex items-center gap-1.5">
            <span class="text-xs font-semibold text-slate-500">From:</span>
            <input
              type="date"
              v-model="customStartDate"
              :max="maxDateLimit"
              @change="handleDateChange"
              class="bg-white text-slate-800 border border-slate-200 rounded-xl px-2.5 py-1.5 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            />
          </div>
          <div class="flex items-center gap-1.5">
            <span class="text-xs font-semibold text-slate-500">To:</span>
            <input
              type="date"
              v-model="customEndDate"
              :min="customStartDate"
              :max="maxDateLimit"
              @change="handleDateChange"
              class="bg-white text-slate-800 border border-slate-200 rounded-xl px-2.5 py-1.5 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            />
          </div>

          <button
            type="button"
            @click="applyCustomDateRange"
            :disabled="!isCustomRangeValid"
            class="px-3 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white font-bold text-xs transition-colors shadow-xs active:scale-95 cursor-pointer disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <i class="fa-solid fa-magnifying-glass text-[11px]"></i>
            Apply
          </button>

          <!-- Range info / warning pill -->
          <span
            v-if="customDaysCount > 0"
            :class="[
              'text-[11px] font-bold px-2 py-0.5 rounded-md border',
              customDaysCount <= 30
                ? 'bg-slate-50 text-slate-600 border-slate-200'
                : 'bg-rose-50 text-rose-600 border-rose-200',
            ]"
          >
            {{ customDaysCount }} {{ customDaysCount === 1 ? 'day' : 'days' }}
            <span v-if="customDaysCount > 30" class="text-rose-600 font-extrabold ml-1">
              (Max 30 days)
            </span>
          </span>
        </div>
      </div>

      <!-- Live Casino badge -->
      <span
        v-if="isLiveCasino"
        class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/80"
      >
        <i class="fa-solid fa-dice text-[11px]"></i> Live Casino Roulette
        Analytics
      </span>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-20">
      <Loader />
    </div>

    <!-- Main Statistics Content -->
    <div v-else-if="playerStats" class="flex flex-col gap-6">
      <!-- 1. Total Overview Section -->
      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between gap-2 flex-wrap">
          <div class="flex items-center gap-2">
            <div
              class="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-sm font-bold shadow-xs"
            >
              <i class="fa-solid fa-chart-column"></i>
            </div>
            <h2
              class="text-sm font-bold text-slate-800 uppercase tracking-wider m-0"
            >
              {{ filterMode === 'custom' ? 'Period Total' : 'Monthly Total' }}
            </h2>
          </div>
          <span
            v-if="filterMode === 'custom' && customStartDate && customEndDate"
            class="text-xs text-slate-500 font-medium"
          >
            {{ formatDateDisplay(customStartDate) }} → {{ formatDateDisplay(customEndDate) }} ({{ customDaysCount }} {{ customDaysCount === 1 ? 'day' : 'days' }})
          </span>
        </div>

      <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <!-- Count Bets -->
        <div
          class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col justify-between gap-1"
        >
          <span
            class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
          >
            <i class="fa-solid fa-play text-blue-500 text-[9px]"></i> Count Bets
          </span>
          <span class="text-lg font-bold text-slate-800">
            {{ playerStats?.total?.totalBets?.toLocaleString() || 0 }}
          </span>
        </div>

        <!-- Count Wins -->
        <div
          class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col justify-between gap-1"
        >
          <span
            class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
          >
            <i class="fa-solid fa-trophy text-emerald-500 text-[9px]"></i> Count
            Wins
          </span>
          <span class="text-lg font-bold text-emerald-600">
            {{ playerStats?.total?.totalWins?.toLocaleString() || 0 }}
          </span>
        </div>

        <!-- Bet Amount -->
        <div
          class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col justify-between gap-1"
        >
          <span
            class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
          >
            <i class="fa-solid fa-coins text-amber-500 text-[9px]"></i> Bet
            Amount
          </span>
          <span class="text-lg font-bold text-blue-600 truncate">
            {{ formatCurrency(playerStats?.total?.totalBetAmount) }}
          </span>
        </div>

        <!-- Win Amount -->
        <div
          class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col justify-between gap-1"
        >
          <span
            class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
          >
            <i
              class="fa-solid fa-money-bill-wave text-purple-500 text-[9px]"
            ></i>
            Win Amount
          </span>
          <span class="text-lg font-bold text-emerald-600 truncate">
            {{ formatCurrency(playerStats?.total?.totalWinAmount) }}
          </span>
        </div>

        <!-- Net Profit -->
        <div
          class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col justify-between gap-1"
        >
          <span
            class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
          >
            <i class="fa-solid fa-chart-line text-emerald-500 text-[9px]"></i>
            Net Profit
          </span>
          <span
            class="text-lg font-bold truncate"
            :class="
              getNetValue(playerStats?.total) >= 0
                ? 'text-emerald-600'
                : 'text-rose-600'
            "
          >
            {{ getNetValue(playerStats?.total) >= 0 ? "+" : ""
            }}{{ formatCurrency(getNetValue(playerStats?.total)) }}
          </span>
        </div>

        <!-- Win Rate -->
        <div
          class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col justify-between gap-1"
        >
          <span
            class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
          >
            <i class="fa-solid fa-percent text-purple-500 text-[9px]"></i> Win
            Rate
          </span>
          <span class="text-lg font-bold text-slate-800">
            {{ getCalculatedWinRate(playerStats?.total) }}%
          </span>
        </div>

        <!-- Live Casino: Fiches Count -->
        <div
          v-if="isLiveCasino && playerStats?.total?.fichesCount !== undefined"
          class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col justify-between gap-1"
        >
          <span
            class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
          >
            <i class="fa-solid fa-circle-dot text-indigo-500 text-[9px]"></i>
            Chips / Fiches
          </span>
          <span class="text-lg font-bold text-slate-800">
            {{ playerStats?.total?.fichesCount?.toLocaleString() || 0 }}
          </span>
        </div>

        <!-- Live Casino: Max Multiplier -->
        <div
          v-if="isLiveCasino && playerStats?.total?.maxMultiplier !== undefined"
          class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col justify-between gap-1"
        >
          <span
            class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
          >
            <i class="fa-solid fa-bolt text-amber-500 text-[9px]"></i> Max
            Multiplier
          </span>
          <span class="text-lg font-bold text-amber-600">
            {{ playerStats?.total?.maxMultiplier || 1 }}x
          </span>
        </div>
      </div>
    </div>

    <!-- ========================================================================= -->
    <!-- LIVE CASINO SPECIFIC SECTIONS -->
    <!-- ========================================================================= -->
    <template v-if="isLiveCasino">
      <!-- 2. Games Breakdown (Dynamic gameId keys) -->
      <div
        v-if="playerStats?.games && Object.keys(playerStats.games).length > 0"
        class="flex flex-col gap-3"
      >
        <div class="flex items-center gap-2">
          <div
            class="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-sm font-bold shadow-xs"
          >
            <i class="fa-solid fa-gamepad"></i>
          </div>
          <h2
            class="text-sm font-bold text-slate-800 uppercase tracking-wider m-0"
          >
            Games Breakdown
          </h2>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div
            v-for="(gameData, gameId) in playerStats.games"
            :key="'game-' + gameId"
            class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col gap-3 shadow-xs"
          >
            <div
              class="flex items-center justify-between pb-2 border-b border-slate-200/60"
            >
              <div class="flex items-center gap-2">
                <div
                  class="w-7 h-7 rounded-lg bg-indigo-100/80 text-indigo-600 flex items-center justify-center text-xs font-bold"
                >
                  <i class="fa-solid fa-dice"></i>
                </div>
                <h3
                  class="text-xs font-bold text-slate-800 m-0 uppercase tracking-tight"
                >
                  {{ formatGameName(gameId) }}
                </h3>
              </div>
              <span
                class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200/70 text-slate-600 font-mono"
              >
                {{ gameId }}
              </span>
            </div>

            <div class="grid grid-cols-2 gap-2 text-xs">
              <div
                class="bg-white border border-slate-100 rounded-xl p-2 flex flex-col"
              >
                <span class="text-[10px] text-slate-400 font-semibold uppercase"
                  >Total Bets</span
                >
                <span class="font-bold text-slate-800">{{
                  gameData.totalBets?.toLocaleString() || 0
                }}</span>
              </div>
              <div
                class="bg-white border border-slate-100 rounded-xl p-2 flex flex-col"
              >
                <span class="text-[10px] text-slate-400 font-semibold uppercase"
                  >Total Wins</span
                >
                <span class="font-bold text-emerald-600">{{
                  gameData.totalWins?.toLocaleString() || 0
                }}</span>
              </div>
              <div
                class="bg-white border border-slate-100 rounded-xl p-2 flex flex-col"
              >
                <span class="text-[10px] text-slate-400 font-semibold uppercase"
                  >Bet Amount</span
                >
                <span class="font-bold text-blue-600 truncate">{{
                  formatCurrency(gameData.totalBetAmount)
                }}</span>
              </div>
              <div
                class="bg-white border border-slate-100 rounded-xl p-2 flex flex-col"
              >
                <span class="text-[10px] text-slate-400 font-semibold uppercase"
                  >Win Amount</span
                >
                <span class="font-bold text-emerald-600 truncate">{{
                  formatCurrency(gameData.totalWinAmount)
                }}</span>
              </div>
            </div>

            <div
              class="flex justify-between items-center pt-2 border-t border-slate-200/60 text-xs"
            >
              <span class="text-slate-400 font-semibold">Net Profit:</span>
              <span
                class="font-bold"
                :class="
                  getNetValue(gameData) >= 0
                    ? 'text-emerald-600'
                    : 'text-rose-600'
                "
              >
                {{ getNetValue(gameData) >= 0 ? "+" : ""
                }}{{ formatCurrency(getNetValue(gameData)) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- 3. Roulette Bet Distribution -->
      <div v-if="playerStats?.roulette" class="flex flex-col gap-4">
        <div class="flex items-center gap-2">
          <div
            class="w-8 h-8 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center text-sm font-bold shadow-xs"
          >
            <i class="fa-solid fa-table-cells"></i>
          </div>
          <h2
            class="text-sm font-bold text-slate-800 uppercase tracking-wider m-0"
          >
            Roulette Bet Distribution
          </h2>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <!-- Inside Bets -->
          <div
            class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col gap-3"
          >
            <h3
              class="text-xs font-bold uppercase tracking-wider text-slate-800 pb-2 border-b border-slate-200/60 m-0 flex items-center gap-2"
            >
              <i class="fa-solid fa-chess-board text-blue-500"></i> Inside Bets
            </h3>
            <div class="flex flex-col divide-y divide-slate-100 text-xs">
              <div
                v-for="(label, key) in insideBetLabels"
                :key="'inside-' + key"
                class="py-2 flex items-center justify-between"
              >
                <span class="font-medium text-slate-600">{{ label }}</span>
                <div class="flex items-center gap-4 text-right">
                  <span class="text-slate-400"
                    >{{
                      playerStats.roulette?.[key]?.totalBets || 0
                    }}
                    bets</span
                  >
                  <span class="font-bold text-slate-800 min-w-[80px]">
                    {{
                      formatCurrency(
                        playerStats.roulette?.[key]?.totalBetAmount,
                      )
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Dozens & Columns -->
          <div
            class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col gap-3"
          >
            <h3
              class="text-xs font-bold uppercase tracking-wider text-slate-800 pb-2 border-b border-slate-200/60 m-0 flex items-center gap-2"
            >
              <i class="fa-solid fa-columns text-emerald-500"></i> Dozens &amp;
              Columns
            </h3>
            <div class="flex flex-col gap-2">
              <!-- Dozens -->
              <span
                class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
                >Dozens (1-12, 13-24, 25-36)</span
              >
              <div class="grid grid-cols-3 gap-2 text-xs">
                <div
                  v-for="d in ['1', '2', '3']"
                  :key="'dozen-' + d"
                  class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col"
                >
                  <span class="text-[10px] font-bold text-slate-500"
                    >{{ d }}° Dozen</span
                  >
                  <span
                    class="font-bold text-slate-800 text-[0.765rem] my-auto"
                  >
                    {{
                      formatCurrency(
                        playerStats.roulette?.dozensColumns?.dozens?.[d]
                          ?.totalBetAmount,
                      )
                    }}
                  </span>
                  <span class="text-[10px] text-slate-400">
                    {{
                      playerStats.roulette?.dozensColumns?.dozens?.[d]
                        ?.totalBets || 0
                    }}
                    bets
                  </span>
                </div>
              </div>

              <!-- Columns -->
              <span
                class="text-[11px] font-bold text-slate-400 uppercase tracking-wider mt-2"
                >Columns</span
              >
              <div class="grid grid-cols-3 gap-2 text-xs">
                <div
                  v-for="c in ['1', '2', '3']"
                  :key="'col-' + c"
                  class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col"
                >
                  <span class="text-[10px] font-bold text-slate-500"
                    >{{ c }}° Column</span
                  >
                  <span
                    class="font-bold text-slate-800 text-[0.765rem] my-auto"
                  >
                    {{
                      formatCurrency(
                        playerStats.roulette?.dozensColumns?.columns?.[c]
                          ?.totalBetAmount,
                      )
                    }}
                  </span>
                  <span class="text-[10px] text-slate-400">
                    {{
                      playerStats.roulette?.dozensColumns?.columns?.[c]
                        ?.totalBets || 0
                    }}
                    bets
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Even Chances (Outside Bets) -->
          <div
            class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col gap-3"
          >
            <h3
              class="text-xs font-bold uppercase tracking-wider text-slate-800 pb-2 border-b border-slate-200/60 m-0 flex items-center gap-2"
            >
              <i
                class="fa-solid fa-arrows-split-up-and-left text-amber-500"
              ></i>
              Even Chances (1:1)
            </h3>
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs h-full">
              <div
                v-for="(label, key) in evenChanceLabels"
                :key="'even-' + key"
                class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col justify-start"
              >
                <span class="text-[11px] font-bold text-slate-700 capitalize">{{
                  label
                }}</span>
                <span class="font-bold text-slate-800 text-[0.765rem] my-auto">
                  {{
                    formatCurrency(
                      playerStats.roulette?.evenChances?.[key]?.totalBetAmount,
                    )
                  }}
                </span>
                <span class="text-[10px] text-slate-400">
                  {{ playerStats.roulette?.evenChances?.[key]?.totalBets || 0 }}
                  bets
                </span>
              </div>
            </div>
          </div>

          <!-- Racetrack & Special Bets -->
          <div
            class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col gap-3"
          >
            <h3
              class="text-xs font-bold uppercase tracking-wider text-slate-800 pb-2 border-b border-slate-200/60 m-0 flex items-center gap-2"
            >
              <i class="fa-solid fa-ring text-purple-500"></i> Racetrack &amp;
              Special Bets
            </h3>
            <div class="grid grid-cols-2 gap-2 text-xs">
              <div
                v-for="(label, key) in racetrackLabels"
                :key="'race-' + key"
                class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col justify-between"
              >
                <span class="text-[11px] font-bold text-slate-700">{{
                  label
                }}</span>
                <span class="font-bold text-slate-800 text-[0.765rem] my-auto">
                  {{
                    formatCurrency(
                      playerStats.roulette?.racetrack?.[key]?.totalBetAmount,
                    )
                  }}
                </span>
                <span class="text-[10px] text-slate-400">
                  {{ playerStats.roulette?.racetrack?.[key]?.totalBets || 0 }}
                  bets
                </span>
              </div>

              <!-- Specials -->
              <div
                v-for="(label, key) in specialLabels"
                :key="'spec-' + key"
                class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col justify-between"
              >
                <span class="text-[11px] font-bold text-slate-700">{{
                  label
                }}</span>
                <span class="font-bold text-slate-800 text-[0.765rem] my-auto">
                  {{
                    formatCurrency(
                      playerStats.roulette?.special?.[key]?.totalBetAmount,
                    )
                  }}
                </span>
                <span class="text-[10px] text-slate-400">
                  {{ playerStats.roulette?.special?.[key]?.totalBets || 0 }}
                  bets
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 4. Daily Breakdown Timeline -->
      <div
        v-if="
          playerStats?.dailyBreakdown && playerStats.dailyBreakdown.length > 0
        "
        class="flex flex-col gap-3"
      >
        <div class="flex items-center gap-2">
          <div
            class="w-8 h-8 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center text-sm font-bold shadow-xs"
          >
            <i class="fa-solid fa-calendar-week"></i>
          </div>
          <h2
            class="text-sm font-bold text-slate-800 uppercase tracking-wider m-0"
          >
            Daily Breakdown
          </h2>
        </div>

        <div
          class="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden"
        >
          <div class="overflow-x-auto">
            <table class="w-full text-left text-xs border-collapse">
              <thead
                class="bg-slate-50 text-slate-500 uppercase text-[10px] font-bold border-b border-slate-200/80"
              >
                <tr>
                  <th class="p-3">Date</th>
                  <th class="p-3 text-center">Bets</th>
                  <th class="p-3 text-center">Wins</th>
                  <th class="p-3 text-right">Bet Amount</th>
                  <th class="p-3 text-right">Win Amount</th>
                  <th class="p-3 text-right">Net Profit</th>
                  <th class="p-3 text-center">Chips</th>
                  <th class="p-3 text-center">Max Multiplier</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                <template
                  v-for="dayItem in playerStats.dailyBreakdown"
                  :key="'day-' + dayItem.day"
                >
                  <tr
                    class="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                    @click="toggleDayExpand(dayItem.day)"
                  >
                    <td
                      class="p-3 font-bold text-slate-800 flex items-center gap-2"
                    >
                      <i
                        class="fa-solid fa-chevron-right text-[10px] text-slate-400 group-hover:text-blue-600 transition-transform"
                        :class="{
                          'rotate-90 text-blue-600': expandedDays.includes(
                            dayItem.day,
                          ),
                        }"
                      ></i>
                      <span>{{ formatDateDisplay(dayItem.day) }}</span>
                    </td>
                    <td class="p-3 text-center font-medium text-slate-700">
                      {{ dayItem.totalBets || 0 }}
                    </td>
                    <td class="p-3 text-center font-bold text-emerald-600">
                      {{ dayItem.totalWins || 0 }}
                    </td>
                    <td class="p-3 text-right font-semibold text-blue-600">
                      {{ formatCurrency(dayItem.totalBetAmount) }}
                    </td>
                    <td class="p-3 text-right font-semibold text-emerald-600">
                      {{ formatCurrency(dayItem.totalWinAmount) }}
                    </td>
                    <td
                      class="p-3 text-right font-bold"
                      :class="
                        getNetValue(dayItem) >= 0
                          ? 'text-emerald-600'
                          : 'text-rose-600'
                      "
                    >
                      {{ getNetValue(dayItem) >= 0 ? "+" : ""
                      }}{{ formatCurrency(getNetValue(dayItem)) }}
                    </td>
                    <td class="p-3 text-center font-medium text-slate-600">
                      {{ dayItem.fichesCount || "-" }}
                    </td>
                    <td class="p-3 text-center font-bold text-amber-600">
                      {{
                        dayItem.maxMultiplier
                          ? dayItem.maxMultiplier + "x"
                          : "-"
                      }}
                    </td>
                  </tr>

                  <!-- Expanded Day Details Sub-row -->
                  <tr
                    v-if="expandedDays.includes(dayItem.day)"
                    :key="'expanded-' + dayItem.day"
                    class="bg-slate-50/50"
                  >
                    <td
                      colspan="8"
                      class="p-4 border-t border-b border-slate-200/60"
                    >
                      <div class="flex flex-col gap-3">
                        <!-- Day Games Breakdown -->
                        <div
                          v-if="
                            dayItem.games &&
                            Object.keys(dayItem.games).length > 0
                          "
                          class="flex flex-col gap-2"
                        >
                          <span
                            class="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5"
                          >
                            <i
                              class="fa-solid fa-gamepad text-indigo-500 text-xs"
                            ></i>
                            Games on {{ formatDateDisplay(dayItem.day) }}
                          </span>
                          <div
                            class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5"
                          >
                            <div
                              v-for="(gameData, gId) in dayItem.games"
                              :key="'day-' + dayItem.day + '-game-' + gId"
                              class="bg-white border border-slate-200/80 rounded-xl p-3 flex flex-col gap-1.5 shadow-2xs"
                            >
                              <div class="flex items-center justify-between">
                                <span
                                  class="font-bold text-slate-800 text-xs"
                                  >{{ formatGameName(gId) }}</span
                                >
                                <span
                                  class="text-[9px] font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-600"
                                  >{{ gId }}</span
                                >
                              </div>
                              <div
                                class="grid grid-cols-2 gap-1 text-[11px] pt-1 border-t border-slate-100"
                              >
                                <div>
                                  <span class="text-slate-400">Bets: </span
                                  ><strong class="text-slate-700">{{
                                    gameData.totalBets || 0
                                  }}</strong>
                                </div>
                                <div>
                                  <span class="text-slate-400">Wins: </span
                                  ><strong class="text-emerald-600">{{
                                    gameData.totalWins || 0
                                  }}</strong>
                                </div>
                                <div>
                                  <span class="text-slate-400">Bet: </span
                                  ><strong class="text-blue-600">{{
                                    formatCurrency(gameData.totalBetAmount)
                                  }}</strong>
                                </div>
                                <div>
                                  <span class="text-slate-400">Win: </span
                                  ><strong class="text-emerald-600">{{
                                    formatCurrency(gameData.totalWinAmount)
                                  }}</strong>
                                </div>
                              </div>
                              <div
                                class="flex justify-between items-center text-[11px] pt-1 border-t border-slate-100"
                              >
                                <span class="text-slate-400">Net:</span>
                                <strong
                                  :class="
                                    getNetValue(gameData) >= 0
                                      ? 'text-emerald-600'
                                      : 'text-rose-600'
                                  "
                                >
                                  {{ getNetValue(gameData) >= 0 ? "+" : ""
                                  }}{{ formatCurrency(getNetValue(gameData)) }}
                                </strong>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Day Roulette Breakdown (if any bets) -->
                        <div
                          v-if="
                            dayItem.roulette &&
                            hasRouletteBets(dayItem.roulette)
                          "
                          class="flex flex-col gap-2 pt-2 border-t border-slate-200/60"
                        >
                          <span
                            class="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5"
                          >
                            <i
                              class="fa-solid fa-dice text-rose-500 text-xs"
                            ></i>
                            Roulette Bets on
                            {{ formatDateDisplay(dayItem.day) }}
                          </span>
                          <div class="flex flex-wrap gap-2 text-xs">
                            <template
                              v-for="(val, bType) in getActiveRouletteBets(
                                dayItem.roulette,
                              )"
                              :key="'btype-' + bType"
                            >
                              <span
                                class="bg-white border border-slate-200/80 px-2.5 py-1 rounded-lg flex items-center gap-2 shadow-2xs"
                              >
                                <span class="font-medium text-slate-700"
                                  >{{ val.label }}:</span
                                >
                                <strong class="text-blue-600"
                                  >{{ val.bets }} bets</strong
                                >
                                <span class="text-slate-400"
                                  >({{ formatCurrency(val.amount) }})</span
                                >
                              </span>
                            </template>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>

    <!-- ========================================================================= -->
    <!-- SLOT / CRASH SPECIFIC SECTIONS -->
    <!-- ========================================================================= -->
    <template v-else>
      <!-- 2. Cashout Analysis Section -->
      <div
        v-if="playerStats.cashout && Object.keys(playerStats.cashout).length"
        class="flex flex-col gap-3"
      >
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-2">
            <div
              class="w-8 h-8 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center text-sm font-bold shadow-xs"
            >
              <i class="fa-solid fa-hand-holding-dollar"></i>
            </div>
            <h2
              class="text-sm font-bold text-slate-800 uppercase tracking-wider m-0"
            >
              Cashout Analysis
            </h2>
          </div>

          <button
            @click="showCashoutGraph = !showCashoutGraph"
            class="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 border border-blue-200/60 font-semibold rounded-xl text-xs flex items-center gap-1.5 transition-all cursor-pointer"
          >
            <i
              :class="
                showCashoutGraph
                  ? 'fa-solid fa-table-cells-large'
                  : 'fa-solid fa-chart-line'
              "
            ></i>
            <span>{{ showCashoutGraph ? "Show Cards" : "Show Graph" }}</span>
          </button>
        </div>

        <!-- Cashout Cards Grid -->
        <div
          v-if="!showCashoutGraph"
          class="grid grid-cols-1 lg:grid-cols-2 gap-4"
        >
          <div
            v-for="(cashout, key) in playerStats.cashout"
            :key="'cashout-' + key"
            class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-5 flex flex-col gap-4"
          >
            <!-- Header -->
            <div
              class="flex items-center justify-between gap-3 pb-3 border-b border-slate-200/60"
            >
              <div class="flex items-center gap-2.5">
                <div
                  class="w-8 h-8 rounded-xl bg-blue-600 text-white flex items-center justify-center text-xs font-bold shadow-xs"
                >
                  {{ key }}
                </div>
                <div>
                  <h3
                    class="text-sm font-bold text-slate-800 m-0 flex items-center gap-2"
                  >
                    <span>Cashout {{ key }}</span>
                    <span
                      class="text-[10px] font-semibold text-slate-400 lowercase"
                      >(total days: {{ cashout?.totalDays || 0 }})</span
                    >
                  </h3>
                  <p class="text-[11px] text-slate-400 m-0 font-medium">
                    Performance Analytics
                  </p>
                </div>
              </div>

              <div class="text-right">
                <div class="text-base font-bold text-slate-800">
                  {{ cashout?.avgCashout?.toFixed(2) || "0.00" }}x
                </div>
                <span
                  class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider"
                  >Avg Multiplier</span
                >
              </div>
            </div>

            <!-- Key Metrics Grid -->
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div
                class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col"
              >
                <span class="text-xs font-bold text-slate-800">{{
                  cashout?.totalBets?.toLocaleString() || 0
                }}</span>
                <span
                  class="text-[10px] text-slate-400 font-semibold uppercase tracking-tight"
                  >Total Bets</span
                >
              </div>
              <div
                class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col"
              >
                <span class="text-xs font-bold text-emerald-600">{{
                  cashout?.totalWins?.toLocaleString() || 0
                }}</span>
                <span
                  class="text-[10px] text-slate-400 font-semibold uppercase tracking-tight"
                  >Total Wins</span
                >
              </div>
              <div
                class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col"
              >
                <span class="text-xs font-bold text-slate-800">
                  {{
                    cashout?.totalWinAmount && cashout?.totalBetAmount
                      ? (
                          (cashout.totalWinAmount / cashout.totalBetAmount) *
                          100
                        ).toFixed(2) + "%"
                      : "0.00%"
                  }}
                </span>
                <span
                  class="text-[10px] text-slate-400 font-semibold uppercase tracking-tight"
                  >Win Rate</span
                >
              </div>
              <div
                class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col"
              >
                <span
                  class="text-xs font-bold text-slate-800 flex items-center justify-between"
                >
                  <span>{{
                    (
                      (cashout?.stdDevCashout || 0) /
                      (cashout?.totalDays > 0 ? cashout.totalDays : 1)
                    ).toFixed(2)
                  }}</span>
                  <i
                    @click="showExplanation"
                    class="fa-solid fa-circle-info text-[10px] text-slate-400 hover:text-blue-500 cursor-pointer"
                  ></i>
                </span>
                <span
                  class="text-[10px] text-slate-400 font-semibold uppercase tracking-tight"
                  >Std Dev</span
                >
              </div>
            </div>

            <!-- Win Distribution Progress Bars -->
            <div class="flex flex-col gap-2 pt-2 border-t border-slate-200/60">
              <h4
                class="text-xs font-bold uppercase tracking-wider text-slate-600 m-0 flex items-center gap-1.5"
              >
                <i class="fa-solid fa-chart-pie text-slate-400 text-[10px]"></i>
                Win Distribution
              </h4>

              <div class="flex flex-col gap-2">
                <div class="flex flex-col gap-1">
                  <div class="flex justify-between text-xs">
                    <span class="text-slate-500 font-medium">Win &lt; 2x</span>
                    <span class="font-bold text-slate-700"
                      >{{ cashout?.count_2X || 0 }} wins ({{
                        (
                          ((cashout?.count_2X || 0) /
                            (cashout?.totalWins > 0 ? cashout.totalWins : 1)) *
                          100
                        ).toFixed(1)
                      }}%)</span
                    >
                  </div>
                  <div
                    class="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden"
                  >
                    <div
                      class="bg-rose-500 h-full rounded-full"
                      :style="{
                        width:
                          ((cashout?.count_2X || 0) /
                            (cashout?.totalWins > 0 ? cashout.totalWins : 1)) *
                            100 +
                          '%',
                      }"
                    ></div>
                  </div>
                </div>

                <div class="flex flex-col gap-1">
                  <div class="flex justify-between text-xs">
                    <span class="text-slate-500 font-medium">Win 2x - 5x</span>
                    <span class="font-bold text-slate-700"
                      >{{ cashout?.count_2X_5X || 0 }} wins ({{
                        (
                          ((cashout?.count_2X_5X || 0) /
                            (cashout?.totalWins > 0 ? cashout.totalWins : 1)) *
                          100
                        ).toFixed(1)
                      }}%)</span
                    >
                  </div>
                  <div
                    class="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden"
                  >
                    <div
                      class="bg-amber-500 h-full rounded-full"
                      :style="{
                        width:
                          ((cashout?.count_2X_5X || 0) /
                            (cashout?.totalWins > 0 ? cashout.totalWins : 1)) *
                            100 +
                          '%',
                      }"
                    ></div>
                  </div>
                </div>

                <div class="flex flex-col gap-1">
                  <div class="flex justify-between text-xs">
                    <span class="text-slate-500 font-medium">Win 5x - 20x</span>
                    <span class="font-bold text-slate-700"
                      >{{ cashout?.count_5X_20X || 0 }} wins ({{
                        (
                          ((cashout?.count_5X_20X || 0) /
                            (cashout?.totalWins > 0 ? cashout.totalWins : 1)) *
                          100
                        ).toFixed(1)
                      }}%)</span
                    >
                  </div>
                  <div
                    class="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden"
                  >
                    <div
                      class="bg-yellow-500 h-full rounded-full"
                      :style="{
                        width:
                          ((cashout?.count_5X_20X || 0) /
                            (cashout?.totalWins > 0 ? cashout.totalWins : 1)) *
                            100 +
                          '%',
                      }"
                    ></div>
                  </div>
                </div>

                <div class="flex flex-col gap-1">
                  <div class="flex justify-between text-xs">
                    <span class="text-slate-500 font-medium"
                      >Win 20x - 50x</span
                    >
                    <span class="font-bold text-slate-700"
                      >{{ cashout?.count_20X_50X || 0 }} wins ({{
                        (
                          ((cashout?.count_20X_50X || 0) /
                            (cashout?.totalWins > 0 ? cashout.totalWins : 1)) *
                          100
                        ).toFixed(1)
                      }}%)</span
                    >
                  </div>
                  <div
                    class="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden"
                  >
                    <div
                      class="bg-emerald-500 h-full rounded-full"
                      :style="{
                        width:
                          ((cashout?.count_20X_50X || 0) /
                            (cashout?.totalWins > 0 ? cashout.totalWins : 1)) *
                            100 +
                          '%',
                      }"
                    ></div>
                  </div>
                </div>

                <div class="flex flex-col gap-1">
                  <div class="flex justify-between text-xs">
                    <span class="text-slate-500 font-medium">Win ≥ 50x</span>
                    <span class="font-bold text-slate-700"
                      >{{ cashout?.count_50X || 0 }} wins ({{
                        (
                          ((cashout?.count_50X || 0) /
                            (cashout?.totalWins > 0 ? cashout.totalWins : 1)) *
                          100
                        ).toFixed(1)
                      }}%)</span
                    >
                  </div>
                  <div
                    class="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden"
                  >
                    <div
                      class="bg-purple-500 h-full rounded-full"
                      :style="{
                        width:
                          ((cashout?.count_50X || 0) /
                            (cashout?.totalWins > 0 ? cashout.totalWins : 1)) *
                            100 +
                          '%',
                      }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Financial Summary footer -->
            <div
              class="grid grid-cols-3 gap-2 pt-2 border-t border-slate-200/60 text-xs"
            >
              <div>
                <span
                  class="text-slate-400 block text-[10px] uppercase font-semibold"
                  >Total Bet</span
                >
                <span class="font-bold text-slate-800 truncate block">{{
                  formatCurrency(cashout?.totalBetAmount)
                }}</span>
              </div>
              <div>
                <span
                  class="text-slate-400 block text-[10px] uppercase font-semibold"
                  >Total Win</span
                >
                <span class="font-bold text-emerald-600 truncate block">{{
                  formatCurrency(cashout?.totalWinAmount)
                }}</span>
              </div>
              <div>
                <span
                  class="text-slate-400 block text-[10px] uppercase font-semibold"
                  >Net Profit</span
                >
                <span
                  class="font-bold truncate block"
                  :class="
                    cashout?.totalWinAmount - cashout?.totalBetAmount >= 0
                      ? 'text-emerald-600'
                      : 'text-rose-600'
                  "
                >
                  {{
                    formatCurrency(
                      (cashout?.totalWinAmount || 0) -
                        (cashout?.totalBetAmount || 0),
                    )
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Graph mode -->
        <div v-else class="flex flex-col gap-4">
          <PlayerStatisticsGraph
            :currency_code="currency_code"
            :rateCurr="rateCurr"
            :data="playerStats"
          />

          <PlayersAvgCashoutGraph
            v-if="playerStats?.avgCashoutPerDays"
            :data="playerStats"
          />
        </div>
      </div>

      <!-- 3. Odd/Even & Range Analysis -->
      <div
        v-if="playerStats.oddeven || playerStats.range"
        class="grid grid-cols-1 lg:grid-cols-2 gap-6"
      >
        <!-- Odd/Even -->
        <div v-if="playerStats.oddeven" class="flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <div
              class="w-8 h-8 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center text-sm font-bold shadow-xs"
            >
              <i class="fa-solid fa-arrows-split-up-and-left"></i>
            </div>
            <h2
              class="text-sm font-bold text-slate-800 uppercase tracking-wider m-0"
            >
              Odd/Even Analysis
            </h2>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div
              v-for="(item, key) in playerStats.oddeven"
              :key="'odd-even-' + key"
              class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col gap-2.5"
            >
              <h3
                class="text-xs font-bold uppercase tracking-wider text-slate-800 pb-2 border-b border-slate-200/60 m-0"
              >
                {{ key.charAt(0).toUpperCase() + key.slice(1) }}
              </h3>
              <div class="flex flex-col gap-1.5 text-xs">
                <div class="flex justify-between">
                  <span class="text-slate-400">Bets:</span
                  ><span class="font-bold text-slate-700">{{
                    item?.totalBets?.toLocaleString() || 0
                  }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-slate-400">Wins:</span
                  ><span class="font-bold text-emerald-600">{{
                    item?.totalWins?.toLocaleString() || 0
                  }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-slate-400">Bet Amount:</span
                  ><span class="font-bold text-slate-700 truncate">{{
                    formatCurrency(item?.totalBetAmount)
                  }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-slate-400">Win Amount:</span
                  ><span class="font-bold text-emerald-600 truncate">{{
                    formatCurrency(item?.totalWinAmount)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Range Analysis -->
        <div v-if="playerStats.range" class="flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <div
              class="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-sm font-bold shadow-xs"
            >
              <i class="fa-solid fa-sliders"></i>
            </div>
            <h2
              class="text-sm font-bold text-slate-800 uppercase tracking-wider m-0"
            >
              Range Analysis
            </h2>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div
              v-for="(item, key) in playerStats.range"
              :key="'range-' + key"
              class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col gap-2.5"
            >
              <h3
                class="text-xs font-bold uppercase tracking-wider text-slate-800 pb-2 border-b border-slate-200/60 m-0 truncate"
              >
                Range {{ Number(key) + 1 }} {{ getRange(Number(key) + 1) }}
              </h3>
              <div class="flex flex-col gap-1.5 text-xs">
                <div class="flex justify-between">
                  <span class="text-slate-400">Bets:</span
                  ><span class="font-bold text-slate-700">{{
                    item?.totalBets?.toLocaleString() || 0
                  }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-slate-400">Wins:</span
                  ><span class="font-bold text-emerald-600">{{
                    item?.totalWins?.toLocaleString() || 0
                  }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-slate-400">Bet Amount:</span
                  ><span class="font-bold text-slate-700 truncate">{{
                    formatCurrency(item?.totalBetAmount)
                  }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-slate-400">Win Amount:</span
                  ><span class="font-bold text-emerald-600 truncate">{{
                    formatCurrency(item?.totalWinAmount)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ========================================================================= -->
    <!-- COMMON: SESSION ANALYTICS -->
    <!-- ========================================================================= -->
    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-2">
        <div
          class="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-sm font-bold shadow-xs"
        >
          <i class="fa-solid fa-clock"></i>
        </div>
        <h2
          class="text-sm font-bold text-slate-800 uppercase tracking-wider m-0"
        >
          Session Analytics
        </h2>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div
          class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col justify-between gap-1"
        >
          <span
            class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
          >
            <i class="fa-solid fa-stopwatch text-amber-500 text-[9px]"></i>
            Session Count
          </span>
          <span class="text-lg font-bold text-slate-800">{{
            playerStats?.sessions?.sessionCount || 0
          }}</span>
          <span class="text-[10px] text-slate-400">Total gaming sessions</span>
        </div>

        <div
          class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col justify-between gap-1"
        >
          <span
            class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
          >
            <i class="fa-solid fa-hourglass-half text-blue-500 text-[9px]"></i>
            Total Play Time
          </span>
          <span class="text-lg font-bold text-blue-600">{{
            formatPlayTime(playerStats?.sessions?.playTime)
          }}</span>
          <span class="text-[10px] text-slate-400"
            >{{
              ((playerStats?.sessions?.playTime || 0) / 1000 / 60).toFixed(1)
            }}
            minutes</span
          >
        </div>

        <div
          class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col justify-between gap-1"
        >
          <span
            class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
          >
            <i class="fa-solid fa-chart-line text-emerald-500 text-[9px]"></i>
            Avg Session Length
          </span>
          <span class="text-lg font-bold text-emerald-600">
            {{
              formatPlayTime(
                (playerStats?.sessions?.playTime || 0) /
                  (playerStats?.sessions?.sessionCount > 0
                    ? playerStats.sessions.sessionCount
                    : 1),
              )
            }}
          </span>
          <span class="text-[10px] text-slate-400">Per session average</span>
        </div>
      </div>
    </div>
  </div>
  </div>

  <!-- Teleported Explanation Modal -->
  <Teleport to="body">
    <div
      v-if="showExplanationModal"
      class="fixed inset-0 z-[999999] bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto"
      @click.self="showExplanation"
    >
      <div
        class="bg-white rounded-3xl shadow-2xl border border-slate-200/80 p-5 md:p-6 w-full max-w-2xl flex flex-col gap-4"
      >
        <div
          class="flex items-center justify-between pb-3 border-b border-slate-100"
        >
          <div class="flex items-center gap-2.5">
            <div
              class="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-sm font-bold"
            >
              <i class="fa-solid fa-circle-question"></i>
            </div>
            <div>
              <h3 class="text-base font-bold text-slate-800 m-0">
                Cashout Standard Deviation
              </h3>
              <p class="text-xs text-slate-400 m-0">
                Understanding player behavior patterns
              </p>
            </div>
          </div>
          <button
            @click="showExplanation"
            class="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center cursor-pointer transition-colors"
          >
            <i class="fa-solid fa-xmark text-sm"></i>
          </button>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div
            class="p-3.5 bg-slate-50 border border-slate-100 rounded-2xl flex flex-col gap-1.5"
          >
            <div class="flex justify-between items-center">
              <span class="text-xs font-bold text-slate-800"
                >Very Consistent</span
              ><span
                class="text-[10px] font-mono font-bold bg-slate-200 px-1.5 py-0.5 rounded"
                >stdDev &lt; 1</span
              >
            </div>
            <p class="text-xs text-slate-500 m-0">
              Robotic-like behavior, cashing out at almost identical odds every
              time (fixed strategy or automated play).
            </p>
          </div>

          <div
            class="p-3.5 bg-slate-50 border border-slate-100 rounded-2xl flex flex-col gap-1.5"
          >
            <div class="flex justify-between items-center">
              <span class="text-xs font-bold text-slate-800"
                >Moderate Variability</span
              ><span
                class="text-[10px] font-mono font-bold bg-slate-200 px-1.5 py-0.5 rounded"
                >1 ≤ stdDev &lt; 3</span
              >
            </div>
            <p class="text-xs text-slate-500 m-0">
              Balanced approach mixing low and medium cashouts with controlled
              risk.
            </p>
          </div>

          <div
            class="p-3.5 bg-slate-50 border border-slate-100 rounded-2xl flex flex-col gap-1.5"
          >
            <div class="flex justify-between items-center">
              <span class="text-xs font-bold text-slate-800"
                >High Variability</span
              ><span
                class="text-[10px] font-mono font-bold bg-slate-200 px-1.5 py-0.5 rounded"
                >3 ≤ stdDev &lt; 6</span
              >
            </div>
            <p class="text-xs text-slate-500 m-0">
              Emotional behavior patterns with significant variations in cashout
              choices.
            </p>
          </div>

          <div
            class="p-3.5 bg-slate-50 border border-slate-100 rounded-2xl flex flex-col gap-1.5"
          >
            <div class="flex justify-between items-center">
              <span class="text-xs font-bold text-slate-800"
                >Extremely Variable</span
              ><span
                class="text-[10px] font-mono font-bold bg-slate-200 px-1.5 py-0.5 rounded"
                >stdDev ≥ 6</span
              >
            </div>
            <p class="text-xs text-slate-500 m-0">
              High-risk behavior chasing very high multipliers with
              all-or-nothing mindset.
            </p>
          </div>
        </div>

        <div class="pt-2 flex justify-end">
          <button
            @click="showExplanation"
            class="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import PlayerStatisticsGraph from "./PlayerStatisticsGraph.vue";
import PlayersAvgCashoutGraph from "./PlayersAvgCashoutGraph.vue";
import { getFetch } from "@/api/axiosClient";
import Loader from "../Loader.vue";
import Swal from "sweetalert2";

export default {
  name: "PlayerStatistics",
  components: {
    Loader,
    PlayerStatisticsGraph,
    PlayersAvgCashoutGraph,
  },
  props: {
    user_id: {
      type: String,
      required: true,
    },
    currency_code: {
      type: String,
      default: "EUR",
    },
    rateCurr: {
      type: Number,
      default: 1,
    },
    category: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      filterMode: "month",
      selectedMonth: "",
      customStartDate: "",
      customEndDate: "",
      last12Months: [],
      isLoading: true,
      showCashoutGraph: false,
      showExplanationModal: false,
      playerStats: null,
      expandedDays: [],
      insideBetLabels: {
        straightUp: "Straight Up (Plein)",
        splits: "Splits (Cheval)",
        streets: "Streets (Transversale)",
        corners: "Corners (Carré)",
        sixLines: "Six Lines (Sixain)",
      },
      evenChanceLabels: {
        red: "Red",
        black: "Black",
        even: "Even",
        odd: "Odd",
        low: "Low (1-18)",
        high: "High (19-36)",
      },
      racetrackLabels: {
        jeuZero: "Jeu Zéro",
        voisins: "Voisins du Zéro",
        orphelins: "Orphelins",
        tiers: "Tiers du Cylindre",
      },
      specialLabels: {
        fullComplete: "Full Complete",
        finales: "Finales",
      },
      defaultStats: {
        avgCashoutPerDays: [],
        cashout: {
          1: {
            avgCashout: 0,
            totalBets: 0,
            totalWins: 0,
            totalBetAmount: 0,
            totalWinAmount: 0,
            totalDays: 0,
            earlyCashoutRate_perWin: 0,
            lateCashoutRate_perWin: 0,
            stdDevCashout: 0,
            count_2X: 0,
            count_2X_5X: 0,
            count_5X_20X: 0,
            count_20X_50X: 0,
            count_50X: 0,
          },
          2: {
            avgCashout: 0,
            totalBets: 0,
            totalWins: 0,
            totalBetAmount: 0,
            totalWinAmount: 0,
            totalDays: 0,
            earlyCashoutRate_perWin: 0,
            lateCashoutRate_perWin: 0,
            stdDevCashout: 0,
            count_2X: 0,
            count_2X_5X: 0,
            count_5X_20X: 0,
            count_20X_50X: 0,
            count_50X: 0,
          },
        },
        oddeven: {
          odd: {
            totalBets: 0,
            totalWins: 0,
            totalBetAmount: 0,
            totalWinAmount: 0,
          },
          even: {
            totalBets: 0,
            totalWins: 0,
            totalBetAmount: 0,
            totalWinAmount: 0,
          },
        },
        range: {
          0: {
            totalBets: 0,
            totalWins: 0,
            totalBetAmount: 0,
            totalWinAmount: 0,
          },
          1: {
            totalBets: 0,
            totalWins: 0,
            totalBetAmount: 0,
            totalWinAmount: 0,
          },
          2: {
            totalBets: 0,
            totalWins: 0,
            totalBetAmount: 0,
            totalWinAmount: 0,
          },
        },
        sessions: { sessionCount: 0, playTime: 0 },
        total: {
          totalBetAmount: 0,
          totalBetAmountConverted: 0,
          totalWinAmount: 0,
          totalBets: 0,
          totalWins: 0,
        },
      },
    };
  },
  computed: {
    isLiveCasino() {
      if (this.category) {
        return this.category.toLowerCase() === "livecasino";
      }
      return (
        this.$route?.meta?.section === "livecasino" ||
        this.$route?.path?.includes("/livecasino") ||
        !!this.playerStats?.roulette
      );
    },
    maxDateLimit() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
    customDaysCount() {
      if (!this.customStartDate || !this.customEndDate) return 0;
      const start = new Date(this.customStartDate);
      const end = new Date(this.customEndDate);
      const diffTime = end.getTime() - start.getTime();
      if (diffTime < 0) return 0;
      return Math.round(diffTime / (1000 * 3600 * 24)) + 1;
    },
    isCustomRangeValid() {
      if (!this.customStartDate || !this.customEndDate) return false;
      const count = this.customDaysCount;
      return count >= 1 && count <= 30;
    },
  },
  watch: {
    category(newCat, oldCat) {
      if (newCat && newCat !== oldCat) {
        this.fetchStatistics();
      }
    },
    user_id(newId, oldId) {
      if (newId && newId !== oldId) {
        this.fetchStatistics();
      }
    },
  },
  mounted() {
    this.generateLast12Months();
    this.selectedMonth = this.last12Months[0].value;

    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const endStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const past = new Date(now);
    past.setDate(past.getDate() - 6);
    const startStr = `${past.getFullYear()}-${pad(past.getMonth() + 1)}-${pad(past.getDate())}`;
    this.customStartDate = startStr;
    this.customEndDate = endStr;

    this.fetchStatistics();
  },
  methods: {
    setFilterMode(mode) {
      if (this.filterMode === mode) return;
      this.filterMode = mode;
      if (mode === "month") {
        this.fetchStatistics();
      } else {
        this.applyCustomDateRange();
      }
    },
    handleDateChange() {
      if (!this.customStartDate || !this.customEndDate) return;
      if (new Date(this.customStartDate) > new Date(this.customEndDate)) {
        this.customEndDate = this.customStartDate;
      }
      if (this.customDaysCount > 30) {
        Swal.fire({
          icon: "warning",
          title: "Period Limit Exceeded",
          text: "The custom date range cannot exceed 30 days. Please select a period within 30 days.",
          confirmButtonColor: "#4f46e5",
        });
      }
    },
    applyCustomDateRange() {
      if (!this.isCustomRangeValid) {
        if (this.customDaysCount > 30) {
          Swal.fire({
            icon: "warning",
            title: "Period Limit Exceeded",
            text: "The custom date range cannot exceed 30 days. Please select a period within 30 days.",
            confirmButtonColor: "#4f46e5",
          });
        }
        return;
      }
      this.fetchStatistics();
    },
    showExplanation() {
      this.showExplanationModal = !this.showExplanationModal;
      document.body.style.overflow = this.showExplanationModal ? "hidden" : "";
    },
    getRange(range) {
      if (range === 1) return "(1.00 - 1.40)";
      if (range === 2) return "(1.41 - 2.80)";
      return "(2.81 - MAX)";
    },
    generateLast12Months() {
      const today = new Date();
      const months = [];

      for (let i = 0; i < 12; i++) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const value = `${year}-${month}-01`;
        const label = date.toLocaleString("en-US", {
          month: "long",
          year: "numeric",
        });
        months.push({ value, label });
      }
      this.last12Months = months;
    },
    async fetchStatistics() {
      try {
        this.isLoading = true;
        const isLive = this.category
          ? this.category.toLowerCase() === "livecasino"
          : (this.$route?.meta?.section === "livecasino" ||
             this.$route?.path?.includes("/livecasino"));
        const baseEndpoint = isLive
          ? "players/statistics-summary-roulette"
          : "players/statistics-summary";

        let queryParams = `user_id=${this.user_id}`;
        if (this.filterMode === "custom") {
          queryParams += `&start_date=${this.customStartDate}&end_date=${this.customEndDate}`;
        } else {
          queryParams += `&month=${this.selectedMonth}`;
        }

        const response = await getFetch(
          `${baseEndpoint}?${queryParams}`,
        );

        if (isLive) {
          if (
            response &&
            typeof response === "object" &&
            (response.total || response.roulette)
          ) {
            this.playerStats = response;
          } else {
            this.playerStats = {
              total: {
                totalBets: 0,
                totalWins: 0,
                totalBetAmount: 0,
                totalWinAmount: 0,
                net: 0,
                fichesCount: 0,
                maxMultiplier: 0,
              },
              sessions: { sessionCount: 0, playTime: 0 },
              roulette: {},
              games: {},
              dailyBreakdown: [],
            };
          }
        } else {
          const requiredKeys = [
            "avgCashoutPerDays",
            "cashout",
            "oddeven",
            "range",
            "sessions",
            "total",
          ];
          const hasAllKeys =
            response &&
            typeof response === "object" &&
            requiredKeys.every((key) => key in response);

          if (!response || !hasAllKeys) {
            this.playerStats = { ...this.defaultStats };
          } else {
            this.playerStats = response;
          }
        }
      } catch (err) {
        console.error("Failed to fetch statistics:", err);
        this.playerStats = { ...this.defaultStats };
      } finally {
        this.isLoading = false;
      }
    },
    formatNumber(num) {
      if (!num) return "0.00";
      return num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatCurrency(amount) {
      if (!amount && amount !== 0)
        return `0.00 ${this.rateCurr !== 1 ? "EUR" : this.currency_code}`;
      return `${this.formatNumber(Number(amount) / (this.rateCurr || 1))} ${
        this.rateCurr !== 1 ? "EUR" : this.currency_code
      }`;
    },
    formatPlayTime(milliseconds) {
      const seconds = milliseconds > 0 ? Math.floor(milliseconds / 1000) : 0;
      const hours = milliseconds > 0 ? Math.floor(seconds / 3600) : 0;
      const minutes = milliseconds > 0 ? Math.floor((seconds % 3600) / 60) : 0;
      const remainingSeconds = milliseconds > 0 ? seconds % 60 : 0;

      if (hours > 0) {
        return `${hours}h ${minutes}m ${remainingSeconds}s`;
      } else if (minutes > 0) {
        return `${minutes}m ${remainingSeconds}s`;
      }
      return `${remainingSeconds}s`;
    },
    getNetValue(data) {
      if (!data) return 0;
      if (data.net !== undefined) return Number(data.net);
      const wins = Number(data.totalWinAmount || 0);
      const bets = Number(data.totalBetAmount || 0);
      return bets - wins;
    },
    getCalculatedWinRate(data) {
      if (!data || !data.totalBets || !data.totalWins) return "0.00";
      return ((Number(data.totalWins) / Number(data.totalBets)) * 100).toFixed(
        2,
      );
    },
    formatGameName(gameId) {
      if (!gameId) return "Game";
      return String(gameId)
        .replace(/([A-Z])/g, " $1")
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim();
    },
    formatDateDisplay(dateString) {
      if (!dateString) return "-";
      const parts = String(dateString).split("-");
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return dateString;
    },
    toggleDayExpand(day) {
      if (this.expandedDays.includes(day)) {
        this.expandedDays = this.expandedDays.filter((d) => d !== day);
      } else {
        this.expandedDays.push(day);
      }
    },
    hasRouletteBets(roulette) {
      if (!roulette || typeof roulette !== "object") return false;
      return Object.keys(this.getActiveRouletteBets(roulette)).length > 0;
    },
    getActiveRouletteBets(roulette) {
      if (!roulette || typeof roulette !== "object") return {};
      const active = {};

      // Inside bets
      for (const [key, label] of Object.entries(this.insideBetLabels)) {
        if (roulette[key] && roulette[key].totalBets > 0) {
          active[key] = {
            label,
            bets: roulette[key].totalBets,
            amount: roulette[key].totalBetAmount,
          };
        }
      }

      // Dozens
      if (roulette.dozensColumns?.dozens) {
        for (const [d, obj] of Object.entries(roulette.dozensColumns.dozens)) {
          if (obj?.totalBets > 0) {
            active[`dozen-${d}`] = {
              label: `${d}° Dozen`,
              bets: obj.totalBets,
              amount: obj.totalBetAmount,
            };
          }
        }
      }

      // Columns
      if (roulette.dozensColumns?.columns) {
        for (const [c, obj] of Object.entries(roulette.dozensColumns.columns)) {
          if (obj?.totalBets > 0) {
            active[`col-${c}`] = {
              label: `${c}° Column`,
              bets: obj.totalBets,
              amount: obj.totalBetAmount,
            };
          }
        }
      }

      // Even Chances
      if (roulette.evenChances) {
        for (const [key, label] of Object.entries(this.evenChanceLabels)) {
          if (roulette.evenChances[key]?.totalBets > 0) {
            active[`even-${key}`] = {
              label,
              bets: roulette.evenChances[key].totalBets,
              amount: roulette.evenChances[key].totalBetAmount,
            };
          }
        }
      }

      // Racetrack
      if (roulette.racetrack) {
        for (const [key, label] of Object.entries(this.racetrackLabels)) {
          if (roulette.racetrack[key]?.totalBets > 0) {
            active[`race-${key}`] = {
              label,
              bets: roulette.racetrack[key].totalBets,
              amount: roulette.racetrack[key].totalBetAmount,
            };
          }
        }
      }

      // Special
      if (roulette.special) {
        for (const [key, label] of Object.entries(this.specialLabels)) {
          if (roulette.special[key]?.totalBets > 0) {
            active[`spec-${key}`] = {
              label,
              bets: roulette.special[key].totalBets,
              amount: roulette.special[key].totalBetAmount,
            };
          }
        }
      }

      return active;
    },
  },
};
</script>
